<template>
  <v-container fluid pa-0 my-3 fill-height>
    <v-card class="project-card fill-height pa-0" style="width: 100%;">
      <v-card-text class="pa-0">
        <v-row class="project-detail-card" v-if="info" no-gutters>

          <!-- Layout horizontal: Hero izquierda + Contenido derecha -->
          <v-col cols="12">
            <div class="card-layout">

              <!-- ── Columna izquierda: Hero con imagen ── -->
              <div class="hero-col">
                <div class="project-hero" :style="`background-image: url(${info.project_image})`">
                  <div class="hero-overlay"></div>

                  <!-- Badge de cerrado -->
                  <div v-if="info.deleted_at && info.status && info.status.user" class="closed-badge">
                    <v-icon x-small color="white" class="mr-1">mdi-lock-outline</v-icon>
                    <span>Cerrado por: {{ info.status.user.name }}</span>
                  </div>

                  <!-- Título del proyecto -->
                  <div class="hero-title">
                    <v-icon class="hero-icon mr-2" small>mdi-office-building-outline</v-icon>
                    <span class="project-name-hero">{{ info.name }}</span>
                  </div>

                  <!-- Contrato sobre la imagen -->
                  <div class="hero-contract">
                    <span class="hero-contract-label">Contrato</span>
                    <span class="hero-contract-value">${{ info.details.contract_value | toThousandFilter }}</span>
                  </div>
                </div>

                <!-- Botones de acción bajo la imagen -->
                <div class="action-bar">
                  <div v-if="info.deleted_at && info.status && info.status.user" class="closed-inline-badge">
                    <v-icon x-small color="#F4516C" class="mr-1">mdi-lock-outline</v-icon>
                    <span>Cerrado</span>
                  </div>
                  <div class="action-bar-buttons">
                    <ProjectSettingsModal :project="info"/>
                    <v-tooltip bottom v-if="checkCompanyLevelPermissions(['status_project'])">
                      <template v-slot:activator="{ on }">
                        <button v-on="on" class="action-pill" :class="info.deleted_at ? 'pill-green' : 'pill-red'" @click="startDeleteProject()">
                          <v-icon small :color="info.deleted_at ? '#3EBFA3' : '#F4516C'">{{ info.deleted_at ? 'mdi-restore' : 'mdi-delete-outline' }}</v-icon>
                          <span>{{ info.deleted_at ? 'Restaurar' : 'Cerrar' }}</span>
                        </button>
                      </template>
                      <span>{{ info.deleted_at ? 'Restaurar proyecto' : 'Cerrar proyecto' }}</span>
                    </v-tooltip>
                    <v-tooltip bottom>
                      <template v-slot:activator="{ on }">
                        <button v-on="on" class="action-pill pill-blue" @click="changeProject()">
                          <v-icon small color="#5E85EF">mdi-swap-horizontal-bold</v-icon>
                          <span>Cambiar</span>
                        </button>
                      </template>
                      <span>Cambiar a este proyecto</span>
                    </v-tooltip>
                  </div>
                </div>
              </div>

              <!-- ── Columna derecha: Métricas ── -->
              <div class="content-col">

                <!-- Nombre del proyecto (header del contenido) -->
                <div class="content-header">
                  <span class="content-project-name">{{ info.name }}</span>
                </div>

                <!-- Grid de módulos 2 columnas -->
                <div class="modules-grid">

                  <!-- Curva S -->
                  <div class="module-card full-width">
                    <div class="module-header">
                      <v-icon x-small color="#2D4B95" class="mr-1">mdi-chart-line-variant</v-icon>
                      <span class="module-title">Control de Avance</span>
                    </div>
                    <div v-if="info.general_project_progress && info.general_project_progress.length" class="progress-chart-wrapper">
                      <PlanningLineChart
                        :rows="info.general_project_progress"
                        :show-legend="false"
                        :show-all-points="false"
                        :series="['planEc', 'realEc', 'reprog']"
                        :chart-height="180"
												:show-overlay="false"
                      />
                    </div>
                    <div v-else class="progress-empty-state">
                      <v-icon small color="#b0bec5">mdi-chart-line-variant</v-icon>
                      <span>Sin datos de avance</span>
                      <v-btn x-small text color="#2D4B95" class="mt-1" @click="goToPlanning">
                        <v-icon x-small left>mdi-arrow-right</v-icon>Ir a Programación
                      </v-btn>
                    </div>
                  </div>

                  <!-- Adicionales -->
                  <div
                    v-if="info.metrics && info.metrics.additionals"
                    class="module-card clickable"
                    @click="goToListing('additional')"
                  >
                    <div class="module-header">
                      <v-icon x-small color="#2D4B95" class="mr-1">mdi-plus-circle-outline</v-icon>
                      <span class="module-title">Adicionales</span>
                    </div>
                    <div class="metrics-row">
                      <div class="metric-item">
                        <div class="chart-wrapper-sm">
                          <DoughnutChart
                            :chartdata="{ datasets: [{ backgroundColor: ['#EAB261', '#F5F5F5'], data: [info.metrics.additionals.total, Math.max(info.metrics.additionals.total * 0.1, 1)], borderWidth: 0 }] }"
                            :options="getChartOptions(info.metrics.additionals.total)"
                          />
                        </div>
                        <p class="chart-label-sm" style="color: #EAB261">TOTAL</p>
                      </div>
                      <div class="metric-item">
                        <div class="chart-wrapper-sm">
                          <DoughnutChart
                            :chartdata="{ datasets: [{ backgroundColor: ['#34BFA3', '#F5F5F5'], data: [info.metrics.additionals.ended, Math.max(info.metrics.additionals.total - info.metrics.additionals.ended, 1)], borderWidth: 0 }] }"
                            :options="getChartOptions(info.metrics.additionals.ended)"
                          />
                        </div>
                        <p class="chart-label-sm" style="color: #34BFA3">RESUELTAS</p>
                      </div>
                      <div class="metric-item">
                        <div class="chart-wrapper-sm">
                          <DoughnutChart
                            :chartdata="{ datasets: [{ backgroundColor: ['#F45771', '#F5F5F5'], data: [info.metrics.additionals.pending, Math.max(info.metrics.additionals.total - info.metrics.additionals.pending, 1)], borderWidth: 0 }] }"
                            :options="getChartOptions(info.metrics.additionals.pending)"
                          />
                        </div>
                        <p class="chart-label-sm" style="color: #F45771">PENDIENTES</p>
                      </div>
                    </div>
                  </div>
                  <div v-else class="module-card empty-module">
                    <v-icon small color="#b0bec5">mdi-plus-circle-outline</v-icon>
                    <span>Sin Adicionales</span>
                  </div>

                  <!-- Tareas -->
                  <div
                    v-if="info.metrics && info.metrics.tasks"
                    class="module-card clickable"
                    @click="goToListing('tasks')"
                  >
                    <div class="module-header">
                      <v-icon x-small color="#2D4B95" class="mr-1">mdi-check-circle-outline</v-icon>
                      <span class="module-title">Tareas</span>
                    </div>
                    <div class="metrics-row">
                      <div class="metric-item">
                        <div class="chart-wrapper-sm">
                          <DoughnutChart
                            :chartdata="{ datasets: [{ backgroundColor: ['#EAB261', '#F5F5F5'], data: [info.metrics.tasks.total, Math.max(info.metrics.tasks.total * 0.1, 1)], borderWidth: 0 }] }"
                            :options="getChartOptions(info.metrics.tasks.total)"
                          />
                        </div>
                        <p class="chart-label-sm" style="color: #EAB261">TOTAL</p>
                      </div>
                      <div class="metric-item">
                        <div class="chart-wrapper-sm">
                          <DoughnutChart
                            :chartdata="{ datasets: [{ backgroundColor: ['#34BFA3', '#F5F5F5'], data: [info.metrics.tasks.ended, Math.max(info.metrics.tasks.total - info.metrics.tasks.ended, 1)], borderWidth: 0 }] }"
                            :options="getChartOptions(info.metrics.tasks.ended)"
                          />
                        </div>
                        <p class="chart-label-sm" style="color: #34BFA3">RESUELTAS</p>
                      </div>
                      <div class="metric-item">
                        <div class="chart-wrapper-sm">
                          <DoughnutChart
                            :chartdata="{ datasets: [{ backgroundColor: ['#F45771', '#F5F5F5'], data: [info.metrics.tasks.pending, Math.max(info.metrics.tasks.total - info.metrics.tasks.pending, 1)], borderWidth: 0 }] }"
                            :options="getChartOptions(info.metrics.tasks.pending)"
                          />
                        </div>
                        <p class="chart-label-sm" style="color: #F45771">PENDIENTES</p>
                      </div>
                    </div>
                  </div>
                  <div v-else class="module-card empty-module">
                    <v-icon small color="#b0bec5">mdi-check-circle-outline</v-icon>
                    <span>Sin Tareas</span>
                  </div>

                  <!-- RDI -->
                  <div
                    v-if="info.metrics && info.metrics.rdi"
                    class="module-card clickable"
                    @click="goToListing('rdi')"
                  >
                    <div class="module-header">
                      <v-icon x-small color="#2D4B95" class="mr-1">mdi-file-document-outline</v-icon>
                      <span class="module-title">RDI</span>
                    </div>
                    <div class="metrics-row">
                      <div class="metric-item">
                        <div class="chart-wrapper-sm">
                          <DoughnutChart
                            :chartdata="{ datasets: [{ backgroundColor: ['#EAB261', '#F5F5F5'], data: [info.metrics.rdi.total, Math.max(info.metrics.rdi.total * 0.1, 1)], borderWidth: 0 }] }"
                            :options="getChartOptions(info.metrics.rdi.total)"
                          />
                        </div>
                        <p class="chart-label-sm" style="color: #EAB261">TOTAL</p>
                      </div>
                      <div class="metric-item">
                        <div class="chart-wrapper-sm">
                          <DoughnutChart
                            :chartdata="{ datasets: [{ backgroundColor: ['#34BFA3', '#F5F5F5'], data: [info.metrics.rdi.ended, Math.max(info.metrics.rdi.total - info.metrics.rdi.ended, 1)], borderWidth: 0 }] }"
                            :options="getChartOptions(info.metrics.rdi.ended)"
                          />
                        </div>
                        <p class="chart-label-sm" style="color: #34BFA3">RESUELTOS</p>
                      </div>
                      <div class="metric-item">
                        <div class="chart-wrapper-sm">
                          <DoughnutChart
                            :chartdata="{ datasets: [{ backgroundColor: ['#F45771', '#F5F5F5'], data: [info.metrics.rdi.pending, Math.max(info.metrics.rdi.total - info.metrics.rdi.pending, 1)], borderWidth: 0 }] }"
                            :options="getChartOptions(info.metrics.rdi.pending)"
                          />
                        </div>
                        <p class="chart-label-sm" style="color: #F45771">PENDIENTES</p>
                      </div>
                    </div>
                  </div>
                  <div v-else class="module-card empty-module">
                    <v-icon small color="#b0bec5">mdi-file-document-outline</v-icon>
                    <span>Sin RDI</span>
                  </div>

                  <!-- Programa -->
                  <div v-if="info.programa" class="module-card full-width">
                    <div class="module-header">
                      <v-icon x-small color="#2D4B95" class="mr-1">mdi-calendar-check-outline</v-icon>
                      <span class="module-title">{{ info.programa.title }}</span>
                      <v-icon :color="info.programa.general_status" small class="ml-auto">mdi-record-circle</v-icon>
                    </div>
                    <div class="programa-row">
                      <div
                        class="programa-item"
                        v-for="(graph, index) in info.programa.graphs"
                        :key="index"
                      >
                        <DoughnutChart
                          :chartdata="{
                            datasets: [{
                              backgroundColor: [graph.color, '#E8EDF5'],
                              data: [graph.value, 100 - graph.value],
                              borderWidth: 0
                            }]
                          }"
                          :options="{
                            tooltips: { enabled: false },
                            cutoutPercentage: 78,
                            responsive: true,
                            maintainAspectRatio: true
                          }"
                        />
                        <span class="donut-label">{{ graph.title }}</span>
                        <span class="donut-value" :style="`color: ${graph.color}`">{{ graph.value }}%</span>
                      </div>
                    </div>
                  </div>
                  <div v-else class="module-card full-width empty-module">
                    <v-icon small color="#b0bec5">mdi-calendar-check-outline</v-icon>
                    <span>Sin información de Programa</span>
                  </div>

                </div>
              </div>
              <!-- fin content-col -->

            </div>
          </v-col>

        </v-row>
      </v-card-text>
    </v-card>
  </v-container>
</template>

<script>
import DoughnutChart from '../../Charts/Doughnut'
import PlanningLineChart from '@/components/Charts/PlanningLineChart'
import ProjectSettingsModal from '@/components/Globals/Modals/Projects/ProjectSettingsModal'

export default {
  name: 'ProjectGridItem',
  components: { DoughnutChart, PlanningLineChart, ProjectSettingsModal },
  props: ['info'],
  data: () => ({}),
  mounted() {},
  methods: {
    async changeProject() {
      const loaded = await this.$store.projectsStore.dispatch('getSelectedProject', this.info.id)
      if (!loaded) return
      this.getConsultasList(this.info.id)
      this.getDashboardInfo()
      window.scrollTo({ top: 0, left: 0, behavior: 'smooth' })
    },

    async goToListing(type) {
      await this.changeProject()
      if (type === 'tasks') {
        this.$router.push({ name: 'Consultas' })
      } else if (type === 'rdi') {
        this.$router.push({ name: 'Rdi' })
      } else if (type === 'additional') {
        this.$router.push({ name: 'Adicionales' })
      }
    },

    async getConsultasList(project_id) {
      if (project_id) {
        this.$store.consultasStore.dispatch('getMyTasks', { project_id })
      }
    },
    getDashboardInfo() {
      this.$store.projectsStore.dispatch('getDashboardInfo')
    },
    startDeleteProject() {
      this.$store.projectsStore.commit('setCloseOpenProjectModal', { status: true, info: this.info })
    },
    async goToPlanning() {
      await this.changeProject()
      this.$router.push({ name: 'Planning' })
    },
    getChartOptions(value) {
      return {
        tooltips: { enabled: false },
        cutoutPercentage: 70,
        responsive: true,
        maintainAspectRatio: true,
        legend: { display: false },
        elements: {
          center: {
            text: (value || 0).toString(),
            color: '#2D4B95',
            fontStyle: 'Arial',
            sidePadding: 20,
            minFontSize: 20,
            lineHeight: 25
          }
        }
      }
    }
  }
}
</script>

<style scoped>
/* ── Tarjeta base ── */
.project-card {
  border-radius: 16px !important;
  overflow: hidden;
  box-shadow: 0 4px 24px rgba(45, 75, 149, 0.10) !important;
  border: 1px solid rgba(45, 75, 149, 0.08);
  transition: box-shadow 0.25s ease, transform 0.25s ease;
}
.project-card:hover {
  box-shadow: 0 8px 36px rgba(45, 75, 149, 0.18) !important;
  transform: translateY(-2px);
}

/* ── Layout horizontal principal ── */
.card-layout {
  display: flex;
  flex-direction: row;
  align-items: stretch;
  min-height: 260px;
}

/* ── Columna izquierda (Hero) ── */
.hero-col {
  width: 160px;
  min-width: 150px;
  flex-shrink: 0;
  display: flex;
  flex-direction: column;
}

.project-hero {
  position: relative;
  flex: 1;
  min-height: 160px;
  background-size: cover;
  background-position: center;
  overflow: hidden;
}
.hero-overlay {
  position: absolute;
  inset: 0;
  background: linear-gradient(
    180deg,
    rgba(20, 38, 90, 0.55) 0%,
    rgba(45, 75, 149, 0.45) 50%,
    rgba(14, 26, 64, 0.82) 100%
  );
}

/* Badge "Cerrado" */
.closed-badge {
  position: absolute;
  top: 10px;
  left: 10px;
  background: rgba(244, 81, 108, 0.85);
  backdrop-filter: blur(4px);
  color: #fff;
  font-size: 10px;
  font-weight: 600;
  letter-spacing: 0.4px;
  padding: 3px 8px;
  border-radius: 20px;
  display: flex;
  align-items: center;
}

/* Título sobre imagen */
.hero-title {
  position: absolute;
  top: 14px;
  left: 12px;
  right: 12px;
  display: flex;
  align-items: flex-start;
  color: #fff;
}
.hero-icon { opacity: 0.85; flex-shrink: 0; margin-top: 1px; }
.project-name-hero {
  font-size: 13px;
  font-weight: 700;
  letter-spacing: 0.3px;
  text-shadow: 0 1px 4px rgba(0,0,0,0.4);
  line-height: 1.3;
  word-break: break-word;
  white-space: normal;
}

/* Contrato dentro de la imagen */
.hero-contract {
  position: absolute;
  bottom: 12px;
  left: 12px;
  right: 12px;
}
.hero-contract-label {
  display: block;
  font-size: 9px;
  font-weight: 600;
  color: rgba(255,255,255,0.65);
  text-transform: uppercase;
  letter-spacing: 0.8px;
  margin-bottom: 2px;
}
.hero-contract-value {
  display: block;
  font-size: 15px;
  font-weight: 800;
  color: #fff;
  text-shadow: 0 1px 6px rgba(0,0,0,0.4);
  letter-spacing: 0.2px;
}

/* Barra de acciones bajo la imagen */
.action-bar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 8px 10px;
  background: #f4f6fb;
  border-top: 1px solid rgba(45, 75, 149, 0.08);
  flex-wrap: wrap;
  gap: 4px;
}
.action-bar-buttons {
  display: flex;
  align-items: center;
  gap: 5px;
  flex-wrap: wrap;
}
.closed-inline-badge {
  display: flex;
  align-items: center;
  font-size: 10px;
  font-weight: 600;
  color: #F4516C;
  letter-spacing: 0.3px;
}

/* Pill buttons */
.action-pill {
  display: inline-flex;
  align-items: center;
  gap: 3px;
  padding: 4px 9px 4px 7px;
  border-radius: 99px;
  border: none;
  cursor: pointer;
  font-size: 10px;
  font-weight: 600;
  letter-spacing: 0.3px;
  transition: filter 0.15s ease, transform 0.15s ease;
  outline: none;
}
.action-pill:hover { filter: brightness(1.08); transform: translateY(-1px); }
.action-pill .v-icon { font-size: 13px !important; }
.pill-blue  { background: rgba(94,133,239,0.13); color: #5E85EF; }
.pill-red   { background: rgba(244,81,108,0.11); color: #F4516C; }
.pill-green { background: rgba(62,191,163,0.13); color: #3EBFA3; }

/* ── Columna derecha (Contenido) ── */
.content-col {
  flex: 1;
  display: flex;
  flex-direction: column;
  border-left: 1px solid rgba(45, 75, 149, 0.08);
  overflow: hidden;
}

.content-header {
  padding: 10px 14px 8px;
  border-bottom: 1px solid rgba(45, 75, 149, 0.08);
  background: linear-gradient(90deg, rgba(45,75,149,0.04), transparent);
}
.content-project-name {
  font-size: 14px;
  font-weight: 700;
  color: #2D4B95;
  letter-spacing: 0.2px;
  display: block;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

/* ── Grid de módulos 2 columnas ── */
.modules-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1px;
  background: rgba(45, 75, 149, 0.08);
  flex: 1;
}

.module-card {
  background: #fff;
  padding: 8px 12px;
  display: flex;
  flex-direction: column;
}
.module-card.full-width {
  grid-column: 1 / -1;
}
.module-card.clickable {
  cursor: pointer;
  transition: background 0.15s ease;
}
.module-card.clickable:hover {
  background: rgba(45, 75, 149, 0.03);
}
.module-card.empty-module {
  flex-direction: row;
  align-items: center;
  gap: 6px;
  font-size: 11px;
  color: #b0bec5;
  font-style: italic;
  min-height: 44px;
}

.module-header {
  display: flex;
  align-items: center;
  margin-bottom: 5px;
}
.module-title {
  font-size: 11px;
  font-weight: 700;
  color: #2D4B95;
  letter-spacing: 0.2px;
}

/* Métricas donut row */
.metrics-row {
  display: flex;
  justify-content: space-around;
  align-items: flex-end;
  gap: 4px;
}
.metric-item {
  display: flex;
  flex-direction: column;
  align-items: center;
  flex: 1;
}

/* Curva S */
.progress-chart-wrapper {
  width: 100%;
  overflow: hidden;
}
.progress-chart-wrapper >>> .plc-root { width: 100%; }
.progress-chart-wrapper >>> .chart-label-bottom { display: none; }
.progress-empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 4px;
  padding: 10px 0;
  font-size: 11px;
  color: #b0bec5;
}

/* Doughnuts compactos */
.chart-wrapper-sm {
  position: relative;
  max-width: 34px;
  margin: 0 auto;
}
/deep/ .chart-wrapper-sm canvas {
  border: none !important;
  box-shadow: none !important;
}
.chart-label-sm {
  font-size: 8px;
  font-weight: 700;
  letter-spacing: 0.3px;
  margin-top: 3px;
  margin-bottom: 0;
  text-align: center;
}

/* Programa */
.programa-row {
  display: flex;
  justify-content: space-around;
  align-items: flex-end;
  gap: 8px;
}
.programa-item {
  display: flex;
  flex-direction: column;
  align-items: center;
  flex: 1;
}
.donut-label {
  font-size: 9px;
  color: #8a9bbd;
  text-align: center;
  margin-top: 2px;
  line-height: 1.2;
}
.donut-value {
  font-size: 11px;
  font-weight: 700;
  text-align: center;
}

/* ── Responsive: en pantallas muy pequeñas vuelve a vertical ── */
@media (max-width: 600px) {
  .card-layout {
    flex-direction: column;
  }
  .hero-col {
    width: 100%;
    min-width: unset;
  }
  .project-hero {
    min-height: 120px;
  }
  .content-col {
    border-left: none;
    border-top: 1px solid rgba(45, 75, 149, 0.08);
  }
}
</style>